/* eslint-disable @typescript-eslint/no-explicit-any */
import {Injectable} from '@angular/core';
import {SpatialUtilService} from '../../design/services/spatial-util.service';
import {PlanModelService} from './plan-model.service';
import {LookupService} from './lookup.service';
import {COMMON_PROPERTY, NON_LOT_COMMON_PROPERTY, PDF_CREATION_METHOD, SP_PREFIXED_PARENT_PLAN_TYPES} from '../constants';
import {PlanHeadingFormat} from '../models/plan-heading-fornat.model';
import {DigitalPlanAttachmentLotNumbersField} from '../../digital-plan/digital-plan.model';

@Injectable({
    providedIn: 'root'
})
export class PlanHeadingService {
    constructor(
        private spatialUtilService: SpatialUtilService,
        private planModelService: PlanModelService,
        private lookupService: LookupService
    ) {}

    private sortAlphaNumeric = (a, b) => a.localeCompare(b, 'en', {numeric: true});
    private pluralTextLookup = () => this.lookupService.getLookups('pluralText');
    private roadSingular = () => this.pluralTextLookup().find(plt => plt.id == 'ROAD').singular;
    private roadPlural = () => this.pluralTextLookup().find(plt => plt.id == 'ROAD').plural;
    private roadWideningSingular = () => this.pluralTextLookup().find(plt => plt.id == 'ROAD_WIDENING').singular;
    private roadWideningPlural = () => this.pluralTextLookup().find(plt => plt.id == 'ROAD_WIDENING').plural;
    private singularInterest = () => this.pluralTextLookup().find(plt => plt.id == 'INTEREST').singular;
    private pluralInterest = () => this.pluralTextLookup().find(plt => plt.id == 'INTEREST').plural;

    getPlanHeading(planHeadingLookup: PlanHeadingFormat): string {
        const permittedNewInterestOrigins = planHeadingLookup.permittedNewInterestOrigins.split(',');
        const planHeadingFormats = planHeadingLookup.planHeadingTypeFormat.split(';');

        let planHeading: string;
        let planHeadingValuesArr = [];

        let allLotsArr = [];

        const planModel = this.planModelService.getModel();
        const isPortalPlan = planModel.pdfCreationMethod === PDF_CREATION_METHOD.SURVEYOR_PORTAL;

        // LOTS
        if (planHeadingFormats.includes('LOTS')) {
            if (isPortalPlan) {
                const allLots = this.spatialUtilService.getLots();
                if (allLots.length) {
                    let lots = allLots
                        .filter(lot => ![5, 10, 11].includes(lot.attributes.type))
                        .map(lot => lot.attributes.name)
                        .map(Number);
                    lots = this.spatialUtilService.groupContinuousNumbers(lots);

                    let roads = allLots
                        .filter(lot => lot.attributes.type == 10)
                        .map(lot => lot.attributes.name)
                        .map(Number);
                    roads = this.spatialUtilService
                        .groupContinuousNumbers(roads)
                        .map(road => (road.includes('-') ? `${road} (${this.roadPlural()})` : `${road} (${this.roadSingular()})`));

                    let roadWidenings = allLots
                        .filter(lot => lot.attributes.type == 11)
                        .map(lot => lot.attributes.name)
                        .map(Number);
                    roadWidenings = this.spatialUtilService
                        .groupContinuousNumbers(roadWidenings)
                        .map(roadWidening =>
                            roadWidening.includes('-')
                                ? `${roadWidening} (${this.roadWideningPlural()})`
                                : `${roadWidening} (${this.roadWideningSingular()})`
                        );

                    const commonPropertyLots = this.spatialUtilService
                        .groupContinuousNumbers(allLots.filter(lot => lot.attributes.type === 5).map(lot => parseInt(lot.attributes.name)))
                        .map(v => `CP${v}`);

                    allLotsArr = lots.concat(roads, roadWidenings);
                    allLotsArr.sort(this.sortAlphaNumeric);
                    allLotsArr.push(...commonPropertyLots);
                    planHeadingValuesArr = planHeadingValuesArr.concat(allLotsArr);
                }
            } else if (planHeadingLookup.parentTenureAssociationType === 'FORMER_TENURE') {
                const formerTenures = planModel.formerTenures;
                if (formerTenures?.length) {
                    const allNewLotNumbers: number[] = [];
                    formerTenures.forEach(ft => {
                        if (!ft.newLotDetails?.fullySupersededByRoad) {
                            allNewLotNumbers.push(...(ft.newLotDetails?.newLots ?? []));
                        }
                        (ft.lots ?? []).forEach(lot => {
                            if (!lot.newLotDetails?.fullySupersededByRoad) {
                                allNewLotNumbers.push(...(lot.newLotDetails?.newLots ?? []));
                            }
                        });
                    });

                    // Digital plans have no spatial data, so the common property, road and road widening lots the plan
                    // creates are the ones declared on the Digital Plan tab rather than being derived from lot type as
                    // they are above.
                    const declaredCommonPropertyLots = this.declaredLots('commonPropertyLots');
                    const declaredRoadLots = this.declaredLots('roadLots');
                    const declaredRoadWideningLots = this.declaredLots('roadWideningLots');
                    const newLotNumbers = [...new Set(allNewLotNumbers)];

                    allLotsArr = this.spatialUtilService
                        .groupContinuousNumbers(
                            newLotNumbers.filter(
                                lot => !declaredCommonPropertyLots.has(lot) && !declaredRoadLots.has(lot) && !declaredRoadWideningLots.has(lot)
                            )
                        )
                        .concat(
                            this.suffixedLots(newLotNumbers, declaredRoadLots, this.roadSingular(), this.roadPlural()),
                            this.suffixedLots(newLotNumbers, declaredRoadWideningLots, this.roadWideningSingular(), this.roadWideningPlural())
                        );
                    allLotsArr.sort(this.sortAlphaNumeric);
                    allLotsArr.push(
                        ...this.spatialUtilService
                            .groupContinuousNumbers(newLotNumbers.filter(lot => declaredCommonPropertyLots.has(lot)))
                            .map(v => `CP${v}`)
                    );
                    planHeadingValuesArr = planHeadingValuesArr.concat(allLotsArr);
                }
            }
        }

        // ROADS
        // A road created as a lot is already named in the lot list, suffixed ROAD, so naming it here as well would name the
        // same road twice. A portal plan cannot double up, the lot owning the geometry and leaving the road no polygon to
        // count, so a declared road lot stands in for that here.
        if (planHeadingFormats.includes('ROAD') && !this.declaredLots('roadLots').size) {
            // A plan without spatial data has no road polygons to count, so its roads are the ones its Plan Information
            // answers name: the new or extended roads and the roads created under a road name approval exemption. The LGA
            // approved unnamed road answer names none, so it is taken as plural, as the road widening answer is below.
            const roadCount = isPortalPlan
                ? this.spatialUtilService.getPolygons().filter(p => p.attributes.type == 10 && p.attributes.roadid).length
                : (planModel.newOrExtendedRoads ? planModel.roads?.length || 0 : 0) +
                  (planModel.approvalExemption ? planModel.exemptedRoads?.filter(exemptedRoad => exemptedRoad.name).length || 0 : 0);
            const roadsPlural = !isPortalPlan && planModel.lgaApprovedUnnamedRoad;

            if (roadsPlural || roadCount > 1) {
                planHeadingValuesArr.push(this.roadPlural());
            } else if (roadCount == 1) {
                planHeadingValuesArr.push(this.roadSingular());
            }
        }

        //ROAD WIDENING
        if (planHeadingFormats.includes('ROAD_WIDENING') && !this.declaredLots('roadWideningLots').size) {
            if (isPortalPlan) {
                const roadWideningPolygons = this.spatialUtilService.getPolygons().filter(p => p.attributes.type == 11 && p.attributes.roadid);
                if (roadWideningPolygons.length == 1) {
                    planHeadingValuesArr.push(this.roadWideningSingular());
                } else if (roadWideningPolygons.length > 1) {
                    planHeadingValuesArr.push(this.roadWideningPlural());
                }
            } else if (planModel.roadWidening) {
                planHeadingValuesArr.push(this.roadWideningPlural());
            }
        }

        // AFFECTED TENURE
        if (planHeadingFormats.includes('PARTIAL_DISCHARGE')) {
            const partialDischargeIntArray = [];
            const notificationTreatmentActionLookup = this.lookupService.getLookups('notificationTreatmentAction');
            if (planModel.formerTenures.length) {
                planModel.formerTenures
                    .filter(ft => !ft.subjectLandType)
                    .forEach(
                        formerTenure =>
                            formerTenure.formerTenureNotifications &&
                            formerTenure.formerTenureNotifications.forEach(formerTenureNotification => {
                                if (
                                    formerTenureNotification.treatmentAction &&
                                    formerTenureNotification.treatmentAction.formerTenureNotificationTreatmentAction === 'PARTIALLY_BROUGHT_FORWARD'
                                ) {
                                    const notificationTreatmentAction = notificationTreatmentActionLookup.find(
                                        ntal => ntal.id === formerTenureNotification.treatmentAction.id
                                    );
                                    if (notificationTreatmentAction && notificationTreatmentAction.description) {
                                        const val = `${notificationTreatmentAction.description.toUpperCase()} OF ${this.singularInterest()}`;
                                        const pluralVal = `${notificationTreatmentAction.description.toUpperCase()} OF ${this.pluralInterest()}`;
                                        if (partialDischargeIntArray.includes(val)) {
                                            if (!partialDischargeIntArray.includes(pluralVal)) {
                                                partialDischargeIntArray.splice(partialDischargeIntArray.indexOf(val), 1, pluralVal);
                                            }
                                        } else if (!partialDischargeIntArray.includes(pluralVal)) {
                                            partialDischargeIntArray.push(val);
                                        }
                                    }
                                }
                            })
                    );
                planHeadingValuesArr = planHeadingValuesArr.concat(partialDischargeIntArray);
            }
        }

        // NEW INTEREST
        let overLOPMap = {};
        if (planModel.interests) {
            const allInterests = planModel.interests
                .filter(
                    interest =>
                        interest.burdenedAffectedTenures.length == 0 ||
                        !interest.burdenedAffectedTenures.every(bat => bat.affectedTenure.subjectLandType)
                )
                .sort((i1, i2) => planHeadingFormats.indexOf(i1.interestCode.type) - planHeadingFormats.indexOf(i2.interestCode.type));

            planHeadingValuesArr = planHeadingValuesArr.concat(this.getNewInterests(allInterests, permittedNewInterestOrigins));

            //OVER LOPS
            if (planHeadingFormats.includes('OVER_LOPS')) {
                overLOPMap = this.getLotsOnPlan(allInterests);
            }
        }

        // JOIN ALL VALUES
        if (planHeadingValuesArr.length > 1) {
            const last = planHeadingValuesArr.pop();
            planHeading = `${planHeadingValuesArr.join(', ')} AND ${last}`;
        } else {
            planHeading = `${planHeadingValuesArr.join(', ')}`;
        }

        if (Object.keys(overLOPMap).length) {
            planHeading = `${planHeading} OVER: ${Object.keys(overLOPMap)
                .map(planNumber => `${overLOPMap[planNumber].join()}/${planNumber}`)
                .join(', ')}`;
        }

        if (allLotsArr.length) {
            planHeading = `LOTS ${planHeading}`;
        }

        return planHeading;
    }

    /**
     * A declaration from the Digital Plan tab, read only for digital plans because a plan that has since changed away from
     * one keeps its stale declarations.
     */
    private declaredLots(field: DigitalPlanAttachmentLotNumbersField): Set<number> {
        const planModel = this.planModelService.getModel();
        return new Set(
            planModel.pdfCreationMethod === PDF_CREATION_METHOD.DIGITAL_PLAN ? (planModel.digitalPlanAttachment?.[field] ?? []) : []
        );
    }

    private suffixedLots(newLotNumbers: number[], declaredLots: Set<number>, singular: string, plural: string): string[] {
        return this.spatialUtilService
            .groupContinuousNumbers(newLotNumbers.filter(lot => declaredLots.has(lot)))
            .map(lotRange => (lotRange.includes('-') ? `${lotRange} (${plural})` : `${lotRange} (${singular})`));
    }

    private getNewInterests(allInterests: any[], permittedNewInterestOrigins: string[]) {
        // get interest type for each interest and count the number for each type
        const interestTypeMap = {};
        allInterests
            .filter(interest => permittedNewInterestOrigins.some(origin => interest.interestCode.origin.split(',').includes(origin)))
            .forEach(interest => {
                if (interest.interestCode && interest.interestCode.type) {
                    if (interestTypeMap[interest.interestCode.type]) {
                        interestTypeMap[interest.interestCode.type]++;
                    } else {
                        interestTypeMap[interest.interestCode.type] = 1;
                    }
                }
            });

        // query PluralTextLookup and retrieve singular and plural value depending upon count
        const interestTypeArr = [];
        Object.keys(interestTypeMap).forEach(key => {
            const pluralText = this.pluralTextLookup().find(ptl => ptl.id == key);
            if (pluralText) {
                if (interestTypeMap[key] === 1) {
                    interestTypeArr.push(pluralText.singular);
                } else {
                    if (pluralText.plural != null) {
                        interestTypeArr.push(pluralText.plural);
                    } else {
                        interestTypeArr.push(pluralText.singular);
                    }
                }
            }
        });

        return interestTypeArr;
    }

    private getLotsOnPlan(allInterests: any[]) {
        // get lots on plan for new interests excluding NLOP
        let overLOPMap = {};
        allInterests.forEach(interest => {
            overLOPMap = interest.burdenedAffectedTenures.reduce((acc, val) => {
                if (val.affectedTenureLot) {
                    const planNumber = SP_PREFIXED_PARENT_PLAN_TYPES.includes(val.affectedTenure.parentPlanType.id)
                        ? 'SP' + val.affectedTenure.parentPlan
                        : val.affectedTenure.parentPlanType.id + val.affectedTenure.parentPlan;
                    if (!acc[planNumber]) {
                        acc[planNumber] = [];
                    }
                    if (val.affectedTenureLot.nonLotCommonProperty) {
                        if (!acc[planNumber].includes(NON_LOT_COMMON_PROPERTY)) {
                            acc[planNumber].push(NON_LOT_COMMON_PROPERTY);
                        }
                    } else {
                        if (val.affectedTenureLot.parentLot) {
                            if (val.affectedTenureLot.parentLotType === COMMON_PROPERTY) {
                                if (!acc[planNumber].includes(COMMON_PROPERTY + val.affectedTenureLot.parentLot)) {
                                    acc[planNumber].push(COMMON_PROPERTY + val.affectedTenureLot.parentLot);
                                }
                            } else {
                                if (!acc[planNumber].includes(val.affectedTenureLot.parentLot)) {
                                    acc[planNumber].push(val.affectedTenureLot.parentLot);
                                }
                            }
                        }
                    }
                }
                return acc;
            }, overLOPMap);
        });
        return overLOPMap;
    }
}
